<section class="flex h-full flex-col rounded-2xl bg-neutral-3 p-4" aria-labelledby="customer-title">
  <header class="mb-2 flex items-center gap-4 px-6 py-2">
    <a
      routerLink=".."
      class="rounded-md p-2 transition-colors hover:bg-gray-200"
      aria-label="Back to customers"
    >
      <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
      </svg>
    </a>
    <h2 id="customer-title" class="text-4xl font-bold">Customer</h2>
  </header>

  <div class="min-h-0 flex-1 space-y-4 overflow-auto">
    @if (customer.error()) {
      <div class="flex flex-col items-center gap-3 p-8 text-center text-gray-500" role="alert">
        <p>Couldn't load the customer.</p>
        <button
          type="button"
          class="rounded-full bg-button-primary px-4 py-2 text-sm text-white shadow hover:bg-button-primary-hover"
          (click)="customer.reload()"
        >
          Try again
        </button>
      </div>
    } @else if (!customer.hasValue()) {
      <div class="space-y-4" aria-busy="true" aria-label="Loading customer">
        <div class="h-28 animate-pulse rounded-2xl bg-white/70"></div>
        <div class="h-24 animate-pulse rounded-2xl bg-white/70"></div>
        <div class="h-48 animate-pulse rounded-2xl bg-white/70"></div>
      </div>
    } @else {
      @let c = customer.value();
      @if (!c) {
        <div class="flex flex-col items-center gap-3 p-8 text-center text-gray-500">
          <p>Customer not found.</p>
          <a routerLink=".." class="text-sm text-button-primary hover:underline">Back to customers</a>
        </div>
      } @else {
        <!-- Profile -->
        <article class="flex items-center gap-4 rounded-2xl bg-white p-6 shadow-sm">
          <app-avatar [src]="c.avatarThumbnailUrl ?? undefined" [name]="c.displayName" [seed]="c.id" size="lg" />
          <div class="min-w-0 flex-1">
            <h3 class="truncate text-3xl font-bold text-gray-900">{{ c.displayName || 'Unknown customer' }}</h3>
            @if (c.email) {
              <a class="block truncate text-gray-500 hover:text-gray-900" [href]="'mailto:' + c.email">{{ c.email }}</a>
            }
            @if (c.isFavorite) {
              <p class="mt-2 inline-flex items-center gap-1 rounded-full bg-amber-50 px-3 py-1 text-sm text-amber-700">
                <span aria-hidden="true">★</span>
                Following
                @if (c.favoritedAt; as since) {
                  since {{ since.toDate() | date: 'd MMM y' }}
                }
              </p>
            }
          </div>
        </article>

        <!-- Totals -->
        <dl class="grid grid-cols-2 gap-4 md:grid-cols-4">
          <div class="rounded-2xl bg-white p-4 shadow-sm">
            <dt class="text-sm text-gray-500">Orders</dt>
            <dd class="text-2xl font-semibold tabular-nums">{{ c.orderCount | number }}</dd>
          </div>
          <div class="rounded-2xl bg-white p-4 shadow-sm">
            <dt class="text-sm text-gray-500">Photos</dt>
            <dd class="text-2xl font-semibold tabular-nums">{{ c.photoCount | number }}</dd>
          </div>
          <div class="rounded-2xl bg-white p-4 shadow-sm">
            <dt class="text-sm text-gray-500">Total spent</dt>
            <dd class="text-2xl font-semibold tabular-nums">{{ spent() | currency: 'EUR' : 'symbol' : '1.2-2' }}</dd>
          </div>
          <div class="rounded-2xl bg-white p-4 shadow-sm">
            <dt class="text-sm text-gray-500">Average order</dt>
            <dd class="text-2xl font-semibold tabular-nums">
              {{ (averageOrder() | currency: 'EUR' : 'symbol' : '1.2-2') ?? '—' }}
            </dd>
          </div>
        </dl>

        <!-- Dates, null while the customer only follows the store -->
        <dl class="grid grid-cols-2 gap-4 rounded-2xl bg-white p-6 shadow-sm">
          <div>
            <dt class="text-sm text-gray-500">First order</dt>
            <dd class="tabular-nums">{{ (c.firstOrderAt?.toDate() | date: 'd MMM y') ?? '—' }}</dd>
          </div>
          <div>
            <dt class="text-sm text-gray-500">Last order</dt>
            <dd class="tabular-nums">{{ (c.lastOrderAt?.toDate() | date: 'd MMM y') ?? '—' }}</dd>
          </div>
        </dl>

        <!-- Printed sizes -->
        <div class="rounded-2xl bg-white p-6 shadow-sm">
          <h3 class="mb-3 text-lg font-semibold">Sizes</h3>
          @if (sizes().length) {
            <table class="w-full text-left">
              <thead class="text-sm text-gray-500">
                <tr>
                  <th scope="col" class="pb-2 font-normal">Size</th>
                  <th scope="col" class="pb-2 text-right font-normal">Orders</th>
                  <th scope="col" class="pb-2 text-right font-normal">Photos</th>
                  <th scope="col" class="pb-2 text-right font-normal">Spent</th>
                </tr>
              </thead>
              <tbody class="tabular-nums">
                @for (size of sizes(); track size.sizeKey) {
                  <tr class="border-t border-gray-100">
                    <th scope="row" class="py-2 font-medium">{{ size.label ?? 'Unknown size' }}</th>
                    <td class="py-2 text-right">{{ size.orders | number }}</td>
                    <td class="py-2 text-right">{{ size.photos | number }}</td>
                    <td class="py-2 text-right">
                      {{ size.revenueCents / 100 | currency: 'EUR' : 'symbol' : '1.2-2' }}
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          } @else {
            <p class="text-gray-500">No printed orders yet.</p>
          }
        </div>

        <!-- This customer's orders with this company -->
        <app-orders [userId]="c.userId" [companyId]="c.companyId" />
      }
    }
  </div>
</section>
